import Component from '@glimmer/component'
import type Store from '@ember-data/store'
import { service } from '@ember/service'
import { task, timeout } from 'ember-concurrency'
import { waitFor } from '@ember/test-waiters'
import type { ShareStatus } from 'teacher-ui-client/models/book-library/share'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import type RouterService from '@ember/routing/router-service'
import type RouteInfo from '@ember/routing/route-info'
import { SchoolClassOptionTypes } from 'teacher-ui-client/components/base/select/school-class/options'
import type SessionService from 'teacher-ui-client/services/session'
import doAction from 'teacher-ui-client/modifiers/do-action'
import perform from 'ember-concurrency/helpers/perform'
import { t } from 'ember-intl'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import type Teacher from 'teacher-ui-client/models/teacher'
import type Student from 'teacher-ui-client/models/student'
import type CollectionStat from 'teacher-ui-client/models/book-library/collection-stat'
import { isPresent } from '@ember/utils'

export class BookLibraryCollectionStats extends Component {
  @service store!: Store

  @service router!: RouterService

  @service session!: SessionService

  @queryParam('subscription-type/book-library/shares/index/by-collection') status!: ShareStatus[]

  @queryParam('subscription-type/book-library/shares/index') schoolClassId!: string | SchoolClassOptionTypes.My

  get query() {
    return {
      filter: {
        status: this.status.join(','),
        ...this.schoolClassFiltersForId(this.schoolClassId),
      },
    }
  }

  /**
   * Based on the selected school class option, constructs the filter for the collection stats query.
   *
   * This component only shows for the Current and Past tabs, which do not have the All Classes option available.
   */
  schoolClassFiltersForId(schoolClassId: string | SchoolClassOptionTypes.My): {
    teacher_ids: string[]
    student_ids?: string[]
  } {
    const isMyClasses = schoolClassId === SchoolClassOptionTypes.My.toString()

    if (isMyClasses) {
      return { teacher_ids: [this.session.currentTeacher.id] }
    } else {
      // this endpoint needs at least one teacher id.
      return {
        teacher_ids: this.teacherIdsForSchoolClassId(schoolClassId),
        student_ids: this.studentIdsForSchoolClassId(schoolClassId),
      }
    }
  }

  teacherIdsForSchoolClassId(schoolClassId: string) {
    const schoolClass = this.store.peekRecord<SchoolClass>('school-class', schoolClassId)
    // @ts-expect-error - something wrong with relationship reference types
    return schoolClass?.hasMany<Teacher, 'teachers'>('teachers').ids().filter(isPresent) ?? []
  }

  studentIdsForSchoolClassId(schoolClassId: string) {
    const schoolClass = this.store.peekRecord<SchoolClass>('school-class', schoolClassId)
    // @ts-expect-error - something wrong with relationship reference types
    return schoolClass?.hasMany<Student, 'students'>('students').ids().filter(isPresent) ?? []
  }

  get collectionStats() {
    return this.queryStats.last?.value
  }

  queryStats = task(
    { restartable: true },
    waitFor(async (query: typeof this.query) => {
      await timeout(100)
      return this.store.queryRecord<CollectionStat>('book-library/collection-stat', query)
    }),
  )

  get showCollectionStats() {
    return (
      this.router.currentRoute?.find((routeInfo: RouteInfo) => routeInfo.name.includes('by-collection')) !== undefined
    )
  }

  <template>
    {{#if this.showCollectionStats}}
      <div class="inline-block" {{doAction (perform this.queryStats this.query)}}>
        {{#if this.collectionStats}}
          <span data-test-stats-loaded class="italic">
            {{t
              "bookLibrary.components.collectionStats.label"
              collectionCount=this.collectionStats.sharedCollectionCount
              bookCount=this.collectionStats.booksInSharedCollectionsCount
            }}
          </span>
        {{/if}}
      </div>
    {{/if}}
  </template>
}

export default BookLibraryCollectionStats
